import { Source } from "@/Common/Models/Domain/Source";
import { Language } from "@/Common/Models/Domain/Language";

export enum StepId {
  WELCOME = "welcome",
  WELCOME_AGAIN = "welcome_again",
  WELCOME_B2B = "welcome_b2b",
  USERNAME = "username",
  THERAPY_TYPE = "therapy_type",
  THERAPY_LANGUAGE = "therapy_language",
  FIRST_TIME = "first_time",
  USER_AGE = "user_age",
  USER_AGE_MINOR = "user_age_minor",
  USER_AGE_MINOR_ADVICE = "user_age_minor_advice",
  USER_AGE_MINOR_REJECT = "user_age_minor_reject",
  USER_GENDER = "user_gender",
  USER_COUPLE_AGE = "user_couple_age",
  USER_COUPLE_GENDER = "user_couple_gender",
  COUPLE_AGE = "couple_age",
  COUPLE_GENDER = "couple_gender",
  COUPLE_SUBAREA = "couple_subarea",
  MAIN_AREA = "main_area",
  SUB_AREA = "sub_area",
  COACHING_SUBAREA = "coaching_sub_area",
  TIME_AFFECTED = "time_affected",
  THERAPIST_NAME = "therapist_name",
  TELL_ME_MORE = "tell_me_more",
  THERAPIST_GENDER_PREFERENCES = "therapist_gender_preferences",
  THERAPIST_AVAILABILITIES = "therapist_availabilities",
  REGISTER = "register",
  EMAIL_VERIFICATION = "email_verification",
  CHECKOUT = "checkout",
  NICE_CHOICE_MY_FRIEND = "nice_choice_my_friend",
  USER_CHOOSE_LAST_THERAPIST = "user_choose_last_therapist",
  THERAPIST_ASSIGNED = "therapist_assigned",
  REGISTER_USER_COME_BACK = "register_user_come_back",
  REGISTER_USER_SSO = "register_user_sso",
  REGISTER_USER_B2B_CUSTOM = "register_user_b2b_custom",
  WELCOME_REASSIGN = "welcome_reassign",
  REASSIGN_REASON = "reassign_reason",
  DETAILS_REASSIGN_CURRENT = "details_reassign_current",
  DETAILS_REASSIGN_SCHEDULE = "details_reassign_schedule",
  DETAILS_REASSIGN_LACK_OF_CONNECTION = "details_reassign_lack_of_connection",
  DETAILS_REASSIGN_POOR_MANAGEMENT = "details_reassign_poor_management",
  ZONE = "zone",
  PRIVACY_POLICY = "privacy_policy",
  EMAIL_CHANGE = "email_change",
}

export interface Option {
  title: string;
  description?: string;
  value: string;
  disabled?: boolean;
  tooltip?: string;
}

export interface ConfigData {
  uuid?: string;
  text: string;
  i18n: string;
  extra?: string;
}

export enum AnswersIds {
  LANGUAGE = "language",
  USERNAME = "username",
  THERAPY_TYPE = "therapy_type",
  THERAPY_LANGUAGE = "therapy_language",
  FIRST_TIME = "first_time",
  USER_GENDER = "user_gender",
  USER_AGE = "user_age",
  COUPLE_AGE = "couple_age",
  COUPLE_GENDER = "couple_gender",
  THERAPIST_AVAILABILITY = "therapist_availability",
  THERAPIST_GENDER_PREFERENCES = "therapist_gender_preferences",
  THERAPIST_NAME = "therapist_name",
  MAIN_AREA = "main_area",
  SUB_AREA = "sub_area",
  TIME_AFFECTED = "time_affected",
  TELL_ME_MORE = "tell_me_more",
  GO_TO_LAST_THERAPIST = "go_to_last_therapist",
  REASSIGN_REASON = "reassign_reason",
  CURRENT = "current",
  DETAILS_REASSIGN = "details_reassign",
  ZONE = "zone",
  PRIVACY_POLICY_AT = "privacy_policy_at",
}

export enum TherapyType {
  SINGLE = "single",
  COUPLE = "couple",
  MINOR = "minor",
  COACHING = "coaching",
}

export enum AdditionalTherapyLanguage {
  CATALAN = "catalan",
  EUSKERA = "euskera",
  GALICIAN = "galician",
}

export enum MinorAgeAnswers {
  ADVICE = "advice",
  REJECT = "reject",
}

export enum YesOrNoAnswer {
  YES = "yes",
  NO = "no",
}

export enum ReasonReassign {
  THERAPIST_LEAVES = "therapist_leaves",
  WRONG_AREA = "wrong_area",
  KNOWN_THERAPIST = "known_therapist",
  SCHEDULE = "schedule",
  CURRENT = "current",
  LACK_OF_CONNECTION = "lack_of_connection",
  POOR_MANAGEMENT = "poor_management",
}
export type Answers = Partial<Record<AnswersIds | string, string | number | boolean>>;

export type RegisterData = {
  email: string;
  password: string;
  fullName: string;
  contactPhone: string;
  isWantTrial: boolean;
  newsLetter: boolean;
  codeType: string;
  privacyPolicyAt: number;
};

export type RegisterB2bData = {
  b2bInfo: {
    documentNumber: string;
    location?: string;
    companySection?: string;
  };
  source: Source;
};

export type FilterChoices = {
  param: keyof Choice;
  value: boolean | string;
  equal?: boolean;
};

export type Component = {
  id: AnswersIds;
  value: string;
  mandatory: boolean;
  name: string;
  type?: string;
  iconName?: string;
  label?: string;
  placeholder?: string;
  minCharacters?: number;
  maxCharacters?: number;
  min?: string;
  max?: string;
  options?: Option[];
  choices?: Choice[] | boolean;
  filterChoices?: FilterChoices;
  choicesName?: ChoicesName;
  counter?: boolean;
};

export enum ChoicesName {
  AVAILABILITIES = "availabilities",
  GENDERS = "genders",
  GENDER_PREFERENCES = "gender_preferences",
  TRIAL = "trial",
  AGE_RANGES = "age_ranges",
  MAIN_AREA = "main_area",
  SUB_AREA = "sub_area",
  COUPLE_SUBAREA = "couple_sub_area",
  LANGUAGES = "languages",
  THERAPY_TYPES = "therapy_types",
  ADDITIONAL_LANGUAGES = "additional_languages",
  REASSIGN_REASONS = "reassign_reasons",
  ZONES = "zones",
  TIME_ZONES = "time_zones",
  DISCHARGE_REASONS = "discharge_reasons",
  MANUAL_SESSION_REASONS = "manual_session_reasons",
  BREAK_REASONS = "break_reasons",
}

export enum ChoicesExtra {
  THERAPIST_KNOW = "known",
  COACHING = "coaching",
  EMPTY_AREA = "empty_area",
}

export type Choice = {
  uuid?: string;
  name?: string;
  text?: string;
  description?: string;
  i18n?: string;
  areaOrder?: number;
  under18?: boolean;
  mainArea?: string;
  mainOnlineEventName?: string;
  isCouple?: boolean;
  extra?: ChoicesExtra | Language | null;
  disabled?: boolean;
  tooltip?: string;
};

export type Subarea = {
  subarea_uuid: string;
  online_event_name: string;
  area_text: string;
  is_couple: boolean;
};

export type Area = {
  area_uuid: string;
  online_event_name: string;
  area_text: string;
  subareas: Subarea[];
  is_couple: boolean;
};

export type InfoDataB2bUsers = {
  title: string;
  value: string;
};

export type TimeZones = {
  value: string;
  label: string;
};

export interface ZoneData extends TimeZones {
  timeZones: TimeZones[];
}

export interface Step {
  title?: string;
  highlightedTitle?: string;
  subtitle?: string;
  setTherapistSubtitleName?: boolean;
  infoField?: string[];
  infoSpecialMainArea?: string;
  username?: string;
  actionButton: boolean;
  components: Component[];
}

export type StepSourceId = Exclude<
  StepId,
  | StepId.WELCOME
  | StepId.REGISTER
  | StepId.USER_AGE_MINOR_ADVICE
  | StepId.USER_AGE_MINOR_REJECT
  | StepId.EMAIL_VERIFICATION
  | StepId.CHECKOUT
  | StepId.NICE_CHOICE_MY_FRIEND
  | StepId.WELCOME_AGAIN
>;

export type StepSource = Record<StepSourceId, Step>;

export type NextStep = {
  id: string;
  nextStep: StepId | null;
};

export type StepMap = Record<Partial<StepId>, NextStep[]>;

export const OnboardingStartFormEventType = "start_form";

export enum RegisterEvent {
  VIEW = "user_registration_view",
  OK = "user_registration",
  KO = "user_registration_failed",
}

export enum EmailValidationEvent {
  VIEW = "email_validation_view",
  OK = "email_validation",
  KO = "email_validation_failed",
}

export enum StepIdWithChoices {
  THERAPY_LANGUAGE = "therapy_language",
  MAIN_AREA = "main_area",
  SUB_AREA = "sub_area",
  REASSIGN_REASON = "reassign_reason",
}

// @ts-expect-error THER-6617
export const OnboardingEventTypes: { [key in StepId]: string } = {
  [StepId.WELCOME]: "welcome",
  [StepId.USERNAME]: "name",
  [StepId.THERAPY_TYPE]: "therapy_type",
  [StepId.THERAPY_LANGUAGE]: "",
  [StepId.FIRST_TIME]: "",
  [StepId.USER_AGE]: "age",
  [StepId.USER_AGE_MINOR]: "age",
  [StepId.USER_AGE_MINOR_ADVICE]: "",
  [StepId.USER_AGE_MINOR_REJECT]: "",
  [StepId.USER_GENDER]: "gender",
  [StepId.USER_COUPLE_AGE]: "age",
  [StepId.USER_COUPLE_GENDER]: "gender",
  [StepId.COUPLE_AGE]: "age_couple",
  [StepId.COUPLE_GENDER]: "gender_couple",
  [StepId.MAIN_AREA]: "area",
  [StepId.SUB_AREA]: "subarea",
  [StepId.COUPLE_SUBAREA]: "subarea",
  [StepId.THERAPIST_NAME]: "",
  [StepId.TIME_AFFECTED]: "",
  [StepId.TELL_ME_MORE]: "text_comment",
  [StepId.THERAPIST_GENDER_PREFERENCES]: "psyco_gender",
  [StepId.THERAPIST_AVAILABILITIES]: "time_availability",
  [StepId.REGISTER]: RegisterEvent.OK,
  [StepId.EMAIL_VERIFICATION]: EmailValidationEvent.OK,
  [StepId.CHECKOUT]: "start_trial",
  [StepId.NICE_CHOICE_MY_FRIEND]: "",
  [StepId.USER_CHOOSE_LAST_THERAPIST]: "",
  [StepId.THERAPIST_ASSIGNED]: "",
  [StepId.WELCOME_AGAIN]: "",
  [StepId.REGISTER_USER_COME_BACK]: "",
  [StepId.REGISTER_USER_SSO]: "",
};

export const AREA_EXTRA_THERAPIST_KNOW = ChoicesExtra.THERAPIST_KNOW;

export const EMPTY_AREA = ChoicesExtra.EMPTY_AREA;

export const SUBAREA_NAME_THERAPIST_KNOWN = "other_known";

export const WITHOUT_THERAPIST = "without_therapist";

export const SUBSCRIPTION_ACTIVE = "subscription_active";

export const SUBSCRIPTION_ACTIVE_THERAPIST_KNOWN = "subscription_active_and_know";

export const SUBSCRIPTION_ACTIVE_LAST_THERAPIST = "subscription_active_and_last_therapist";

export const B2B_USER = "b2b_user";

export const CUSTOM_B2B_USER = "custom_b2b_user";

export const AREA_NAME_COACHING = "coaching_1";

export const AREA_NAME_COUPLE = "couple";

export const WELLHUB_USER = "wellhub_user";

export const MIN_AGE = "18";

export const MAX_AGE_UNDER_18 = "17";

export const LIMIT_AGE = "100";

export const OTHER_LOCATION = "other";

export const PRIVACY_POLICY_EMPTY = "privacy_policy_empty";

export const EMAIL_CHANGE_REQUIRED = "email_change_required";
export interface B2bUser {
  source: Source;
  language: string;
  email: string;
  codeType: string | undefined;
}
